import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
    X,
    Users,
    Calendar,
    MapPin,
    Trophy,
    BarChart3,
    Save,
    Loader2,
    Plus,
    CheckCircle2,
    ChevronDown,
    AlertCircle,
    ClipboardPen
} from 'lucide-react';
import { getSportConfig, calculateSetScore } from '../../config/sportStats';
import './GameDetailModal.css';
import API_BASE_URL from '../../config/api';

/**
 * Modal for viewing/editing a game's result and athlete statistics.
 */
const GameDetailModal = ({ isOpen, onClose, game, sportName, initialTab = 'result', focusAthleteId, readOnly = false }) => {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [homeScore, setHomeScore] = useState('');
    const [awayScore, setAwayScore] = useState('');
    const [sets, setSets] = useState([]);
    const [isFinished, setIsFinished] = useState(false);
    const [notes, setNotes] = useState('');
    const [localIsHomeGame, setLocalIsHomeGame] = useState(game?.isHomeGame ?? true);
    const [localOpponentName, setLocalOpponentName] = useState(game?.opponentName || '');

    const [calledUpAthletes, setCalledUpAthletes] = useState([]);
    const [athleteStats, setAthleteStats] = useState({});
    const [expandedAthletes, setExpandedAthletes] = useState(new Set());

    const [apiSportName, setApiSportName] = useState('');
    const sportConfig = getSportConfig(sportName, apiSportName);

    useEffect(() => {
        if (isOpen && game?.id) {
            setActiveTab(initialTab);
            setLocalIsHomeGame(game?.isHomeGame ?? true);
            setLocalOpponentName(game?.opponentName || '');
            fetchGameData();
        }
    }, [isOpen, game?.id, initialTab]);

    useEffect(() => {
        if (focusAthleteId) {
            setExpandedAthletes(prev => new Set(prev).add(focusAthleteId));
        }
    }, [focusAthleteId]);

    const fetchGameData = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/game-results/${game.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Falha ao carregar dados do jogo.');
            const data = await res.json();

            if (data.isHomeGame !== undefined) setLocalIsHomeGame(data.isHomeGame);
            if (data.opponentName !== undefined) setLocalOpponentName(data.opponentName);
            setApiSportName(data.sportName || '');

            if (data.result) {
                setHomeScore(data.result.homeScore ?? '');
                setAwayScore(data.result.awayScore ?? '');
                setIsFinished(data.result.isFinished ?? false);
                setNotes(data.result.notes ?? '');
                if (data.result.setsJson) {
                    try { setSets(JSON.parse(data.result.setsJson)); } catch { setSets([]); }
                } else {
                    setSets([]);
                }
            } else {
                setHomeScore('');
                setAwayScore('');
                setIsFinished(false);
                setNotes('');
                setSets([]);
            }

            setCalledUpAthletes(data.calledUpAthletes || []);

            const statsMap = {};
            (data.athleteStats || []).forEach(s => {
                try { statsMap[s.athleteId] = JSON.parse(s.statsJson); }
                catch { statsMap[s.athleteId] = {}; }
            });
            setAthleteStats(statsMap);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                homeScore: sportConfig.type === 'score'
                    ? (homeScore !== '' ? parseInt(homeScore) : null)
                    : (sportConfig.type === 'sets' && sets.length > 0 ? homeSets : null),
                awayScore: sportConfig.type === 'score'
                    ? (awayScore !== '' ? parseInt(awayScore) : null)
                    : (sportConfig.type === 'sets' && sets.length > 0 ? awaySets : null),
                setsJson: sportConfig.type === 'sets' && sets.length > 0 ? JSON.stringify(sets) : null,
                isFinished,
                notes: notes || null,
                athleteStats: Object.keys(athleteStats).map(aId => ({
                    athleteId: parseInt(aId),
                    statsJson: JSON.stringify(athleteStats[aId] || {})
                }))
            };

            const res = await fetch(`${API_BASE_URL}/api/game-results/${game.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Falha ao guardar resultado.');
            window.dispatchEvent(new CustomEvent('dashboardRefresh'));
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const updateAthleteStat = (athleteId, key, value) => {
        setAthleteStats(prev => ({
            ...prev,
            [athleteId]: { ...(prev[athleteId] || {}), [key]: value }
        }));
    };

    const toggleAthleteExpand = (athleteId) => {
        setExpandedAthletes(prev => {
            const next = new Set(prev);
            if (next.has(athleteId)) next.delete(athleteId);
            else next.add(athleteId);
            return next;
        });
    };

    const addSet = () => setSets(prev => [...prev, { home: '', away: '' }]);
    const removeSet = (idx) => setSets(prev => prev.filter((_, i) => i !== idx));
    const updateSet = (idx, side, value) => {
        setSets(prev => prev.map((s, i) => i === idx ? { ...s, [side]: value } : s));
    };

    const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?';

    const { homeSets, awaySets } = calculateSetScore(sets.map(s => ({
        home: parseInt(s.home) || 0,
        away: parseInt(s.away) || 0
    })));

    const gameDate = game ? new Date(game.startDateTime) : null;

    if (!isOpen) return null;

    const homeLabel = localIsHomeGame ? 'Nós' : (localOpponentName || 'Visitante');
    const awayLabel = localIsHomeGame ? (localOpponentName || 'Adversário') : 'Nós';

    return ReactDOM.createPortal(
        <div className="gdm-overlay" onClick={onClose}>
            <div className="gdm-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="gdm-header">
                    <div className="gdm-header-inner">
                        <div className="gdm-header-top">
                            <div className="title-section">
                                <div className="sport-badge">{sportName}</div>
                                <h2>{game?.title || 'Detalhes do Jogo'}</h2>
                            </div>
                            <button className="gdm-close" onClick={onClose} aria-label="Fechar">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="gdm-header-meta">
                            <div className="meta-item">
                                <Calendar size={13} />
                                <span>{gameDate?.toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                            </div>
                            {game?.location && (
                                <div className="meta-item">
                                    <MapPin size={13} />
                                    <span>{game.location}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Matchup bar */}
                    {localOpponentName && (
                        <div className="gdm-matchup-bar">
                            <div className={`matchup-side ${localIsHomeGame ? 'home' : ''}`}>
                                <span className="matchup-side-label">{localIsHomeGame ? 'Casa' : 'Fora'}</span>
                                <span className="matchup-side-name">{homeLabel}</span>
                            </div>
                            <div className="matchup-vs">VS</div>
                            <div className={`matchup-side ${!localIsHomeGame ? 'home' : ''}`}>
                                <span className="matchup-side-label">{localIsHomeGame ? 'Fora' : 'Casa'}</span>
                                <span className="matchup-side-name">{awayLabel}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="gdm-tabs">
                    <button
                        className={`gdm-tab ${activeTab === 'result' ? 'active' : ''}`}
                        onClick={() => setActiveTab('result')}
                    >
                        <Trophy size={17} />
                        <span>Resultado</span>
                    </button>
                    <button
                        className={`gdm-tab ${activeTab === 'stats' ? 'active' : ''}`}
                        onClick={() => setActiveTab('stats')}
                    >
                        <BarChart3 size={17} />
                        <span>Estatísticas</span>
                        {calledUpAthletes.length > 0 && (
                            <span className="tab-count">{calledUpAthletes.length}</span>
                        )}
                    </button>
                </div>

                {/* Body */}
                <div className="gdm-body">
                    {loading ? (
                        <div className="gdm-loading">
                            <Loader2 className="spinner" />
                            <span>A carregar dados...</span>
                        </div>
                    ) : error ? (
                        <div className="gdm-error">
                            <AlertCircle size={32} />
                            <span>{error}</span>
                        </div>
                    ) : activeTab === 'result' ? (
                        <ResultTab
                            sportConfig={sportConfig}
                            homeScore={homeScore} setHomeScore={setHomeScore}
                            awayScore={awayScore} setAwayScore={setAwayScore}
                            sets={sets}
                            addSet={addSet} removeSet={removeSet} updateSet={updateSet}
                            homeSets={homeSets} awaySets={awaySets}
                            isFinished={isFinished} setIsFinished={setIsFinished}
                            notes={notes} setNotes={setNotes}
                            opponentName={localOpponentName}
                            isHomeGame={localIsHomeGame}
                            readOnly={readOnly}
                        />
                    ) : (
                        <StatsTab
                            athletes={calledUpAthletes}
                            athleteStats={athleteStats}
                            sportConfig={sportConfig}
                            expandedAthletes={expandedAthletes}
                            toggleAthleteExpand={toggleAthleteExpand}
                            updateAthleteStat={updateAthleteStat}
                            getInitials={getInitials}
                            readOnly={readOnly}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="gdm-footer">
                    <button className="gdm-btn-secondary" onClick={onClose} disabled={saving}>
                        Fechar
                    </button>
                    {!readOnly && (
                        <button className="gdm-btn-primary" onClick={handleSave} disabled={saving || loading}>
                            {saving ? (
                                <>
                                    <Loader2 size={18} className="spinner-small" />
                                    <span>A guardar...</span>
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    <span>Guardar</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

/* ── Result Tab ── */
const ResultTab = ({ sportConfig, homeScore, setHomeScore, awayScore, setAwayScore, sets, addSet, removeSet, updateSet, homeSets, awaySets, isFinished, setIsFinished, notes, setNotes, opponentName, isHomeGame, readOnly }) => {
    const homeLabel = isHomeGame ? 'Nós' : opponentName || 'Visitante';
    const awayLabel = isHomeGame ? opponentName || 'Adversário' : 'Nós';

    return (
        <div className="gdm-result-container">
            {sportConfig.type === 'score' && (
                <div className="gdm-section">
                    <div className="section-header">
                        <Trophy size={16} />
                        <h3>Marcador</h3>
                    </div>
                    <div className="gdm-score-board">
                        <div className="score-team">
                            <span className="team-name">{homeLabel}</span>
                            <input
                                className="score-input"
                                type="number" min="0"
                                value={homeScore}
                                onChange={e => setHomeScore(e.target.value)}
                                placeholder="0"
                                disabled={readOnly}
                            />
                        </div>
                        <div className="score-divider">:</div>
                        <div className="score-team">
                            <span className="team-name">{awayLabel}</span>
                            <input
                                className="score-input"
                                type="number" min="0"
                                value={awayScore}
                                onChange={e => setAwayScore(e.target.value)}
                                placeholder="0"
                                disabled={readOnly}
                            />
                        </div>
                    </div>
                </div>
            )}

            {sportConfig.type === 'sets' && (
                <div className="gdm-section">
                    <div className="section-header">
                        <ClipboardPen size={16} />
                        <h3>Sets / Parciais</h3>
                    </div>

                    <div className="gdm-sets-grid">
                        {sets.length > 0 && (
                            <div className="sets-summary">
                                <span className={homeSets > awaySets ? 'winner' : ''}>{homeSets}</span>
                                <span className="divider">–</span>
                                <span className={awaySets > homeSets ? 'winner' : ''}>{awaySets}</span>
                            </div>
                        )}

                        <div className="sets-list">
                            {sets.map((set, idx) => (
                                <div className="set-row" key={idx}>
                                    <div className="set-number">{idx + 1}º</div>
                                    <div className="set-inputs">
                                        <input
                                            type="number" min="0"
                                            value={set.home}
                                            onChange={e => updateSet(idx, 'home', e.target.value)}
                                            placeholder="0"
                                            disabled={readOnly}
                                        />
                                        <span className="inner-divider">-</span>
                                        <input
                                            type="number" min="0"
                                            value={set.away}
                                            onChange={e => updateSet(idx, 'away', e.target.value)}
                                            placeholder="0"
                                            disabled={readOnly}
                                        />
                                    </div>
                                    {!readOnly && (
                                        <button className="remove-set" onClick={() => removeSet(idx)}>
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {!readOnly && sets.length < (sportConfig.maxSets || 7) && (
                            <button className="add-set-btn" onClick={addSet}>
                                <Plus size={16} />
                                <span>Adicionar Set</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="gdm-section">
                <div className="status-toggle-row">
                    <label className="checkbox-container">
                        <input
                            type="checkbox"
                            checked={isFinished}
                            onChange={e => setIsFinished(e.target.checked)}
                            disabled={readOnly}
                        />
                        <span className="checkmark"></span>
                        <span className="label-text">Jogo concluído / resultado oficial</span>
                    </label>
                </div>

                <div className="notes-area">
                    <label>Notas Adicionais</label>
                    <textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder={readOnly ? "" : "Observações sobre o jogo, incidentes ou notas do treinador..."}
                        disabled={readOnly}
                    />
                </div>
            </div>
        </div>
    );
};

/* ── Stats Tab ── */
const StatsTab = ({ athletes, athleteStats, sportConfig, expandedAthletes, toggleAthleteExpand, updateAthleteStat, getInitials, readOnly }) => {
    if (!athletes || athletes.length === 0) {
        return (
            <div className="gdm-empty-state">
                <Users size={48} className="empty-icon" />
                <h3>Sem Convocatória</h3>
                <p>Não existem atletas convocados para este jogo. Defina a convocatória no Histórico primeiro.</p>
            </div>
        );
    }

    return (
        <div className="gdm-athlete-stats-list">
            {athletes.map(athlete => {
                const isExpanded = expandedAthletes.has(athlete.id);
                const stats = athleteStats[athlete.id] || {};

                return (
                    <div className={`athlete-stat-card ${isExpanded ? 'active' : ''}`} key={athlete.id}>
                        <div className="card-header" onClick={() => toggleAthleteExpand(athlete.id)}>
                            <div className="athlete-profile">
                                <div className="avatar-small">{getInitials(athlete.name)}</div>
                                <span className="athlete-name">{athlete.name}</span>
                            </div>
                            <div className="card-controls">
                                {Object.keys(stats).length > 0 && (
                                    <span className="stat-count-badge">
                                        {Object.keys(stats).length} registos
                                    </span>
                                )}
                                <ChevronDown size={18} className={`arrow ${isExpanded ? 'open' : ''}`} />
                            </div>
                        </div>

                        {isExpanded && (
                            <div className="card-body">
                                <div className="stats-grid">
                                    {sportConfig.stats.map(field => {
                                        const val = stats[field.key] ?? '';
                                        const isCheckbox = field.inputType === 'checkbox';

                                        return (
                                            <div
                                                key={field.key}
                                                className={`stat-input-group ${isCheckbox ? 'full-width' : ''}`}
                                            >
                                                {isCheckbox ? (
                                                    <label className="checkbox-container mini">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!val}
                                                            onChange={e => updateAthleteStat(athlete.id, field.key, e.target.checked)}
                                                            disabled={readOnly}
                                                        />
                                                        <span className="checkmark"></span>
                                                        <span className="label-text">{field.label}</span>
                                                    </label>
                                                ) : (
                                                    <>
                                                        <label>{field.label}{field.unit ? ` (${field.unit})` : ''}</label>
                                                        <input
                                                            type={field.inputType}
                                                            min="0"
                                                            value={val}
                                                            placeholder={field.placeholder || '0'}
                                                            onChange={e => updateAthleteStat(athlete.id, field.key, e.target.value)}
                                                            disabled={readOnly}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default GameDetailModal;
